
import React from 'react';
import { ChevronLeft, Info, Zap, Sparkles, Brain, Radiation, Microscope, Ghost, Star } from 'lucide-react';
import { AppView } from '../types.ts';

interface RarityGuideViewProps {
  setActiveView: (view: AppView) => void;
}

export default function RarityGuideView({ setActiveView }: RarityGuideViewProps) {
  
  const getRarityStyles = (rarity: string) => {
    switch (rarity) {
      case 'uncommon': return 'border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.3)] bg-emerald-500/5';
      case 'rare': return 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.4)] bg-blue-500/5';
      case 'epic': return 'border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.4)] bg-purple-500/5';
      case 'legendary': return 'border-yellow-400 shadow-[0_0_20px_rgba(250,196,21,0.5)] bg-yellow-400/5';
      case 'mythic': return 'border-red-500 animate-pulse shadow-[0_0_25px_rgba(239,68,68,0.5)] bg-red-500/5';
      case 'god': return 'animate-rainbow-border border-white shadow-[0_0_35px_rgba(255,255,255,0.8)] bg-white/5';
      default: return 'border-zinc-800 bg-zinc-900/50';
    }
  };

  const TIERS = [
    { name: 'Common', range: '1-20', label: 'THE BASELINE', rarity: 'common', description: 'Standard brainrot. Low aura, but everyone starts here.' },
    { name: 'Uncommon', range: '21-35', label: 'THE ITCH', rarity: 'uncommon', description: 'Slightly more chaotic signals detected.' },
    { name: 'Rare', range: '36-50', label: 'THE RIZZLER', rarity: 'rare', description: 'High aura and clean energy. Worth showcasing.' },
    { name: 'Epic', range: '51-65', label: 'NEURAL DRIFT', rarity: 'epic', description: 'Your brain starts slipping into the rot.' },
    { name: 'Legendary', range: '66-80', label: 'SIGMA STATUS', rarity: 'legendary', description: 'Golden frequencies. Absolute status.' },
    { name: 'Mythic', range: '81-94', label: 'OHIO SPECIAL', rarity: 'mythic', description: 'Cursed levels of rot. Reality is breaking.' },
    { name: 'God', range: '95-100', label: 'ASCENDANT', rarity: 'god', description: 'The absolute peak. You are the source of the rot.' },
  ];

  const FREQUENCIES = [
    { name: 'Cognitive Damage', icon: <Radiation size={20}/>, color: 'text-yellow-400', desc: 'How much your brain breaks when looking at the rot. Peak levels are found at 3 AM.' },
    { name: 'Signal Purity', icon: <Zap size={20}/>, color: 'text-blue-400', desc: 'Is it chaos or a clear masterpiece? High purity means the rot is dangerously sharp.' },
    { name: 'Thoughtform', icon: <Brain size={20}/>, color: 'text-purple-400', desc: 'The concept behind the rot. From "Dead Memes" to "Reality Errors."' },
    { name: 'Origin Event', icon: <Microscope size={20}/>, color: 'text-emerald-400', desc: 'When and how it was cooked. Late-night cooks have higher anomaly chances.' },
    { name: 'Rot Signature', icon: <Ghost size={20}/>, color: 'text-indigo-400', desc: 'The final ID marker. Getting a "Godmark" signature is a 0.1% event.' },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-12 animate-in slide-in-from-bottom-8">
      {/* Header */}
      <div className="bg-zinc-950/60 backdrop-blur-xl border-2 border-white/5 rounded-[3rem] p-10 shadow-2xl space-y-4">
        <div className="flex items-center gap-4">
          <button onClick={() => setActiveView('editor')} className="p-3 bg-zinc-900 rounded-2xl hover:scale-110 active:scale-90 transition-transform shadow-lg">
            <ChevronLeft size={24}/>
          </button>
          <div className="flex flex-col">
            <h2 className="text-5xl font-bungee text-yellow-400 uppercase tracking-tighter leading-none">THE SCIENCE OF ROT</h2>
            <p className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.3em] px-1">How your aura is measured</p>
          </div>
        </div>
        <p className="text-sm font-bold text-zinc-300 max-w-2xl leading-relaxed">
          Every time you BLAST a new image, our Neural Engine scans 5 Forbidden Frequencies to calculate your **Rot Score**. The higher the score, the higher your Aura.
        </p>
      </div>

      {/* Rarity Tiers */}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-l-4 border-yellow-400 pl-4 py-1">
          <Star size={18} className="text-yellow-400" />
          <h3 className="font-bungee text-2xl uppercase tracking-widest">RARITY TIERS</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TIERS.map(tier => (
            <div key={tier.name} className={`p-8 rounded-[2.5rem] border-4 flex flex-col gap-4 transition-all hover:translate-y-[-8px] group ${getRarityStyles(tier.rarity)}`}>
              <div className="flex justify-between items-start">
                <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">{tier.range} SCORE</span>
                <span className={`font-bungee text-[8px] uppercase px-2 py-0.5 rounded border ${tier.rarity === 'god' ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                  {tier.label}
                </span>
              </div>
              <h4 className="text-3xl font-bungee uppercase tracking-tight group-hover:scale-110 transition-transform origin-left">{tier.name}</h4>
              <p className="text-[10px] font-bold text-zinc-400 uppercase leading-relaxed tracking-wide">
                {tier.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Frequency Explanation */}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-l-4 border-indigo-500 pl-4 py-1">
          <Zap size={18} className="text-indigo-500" />
          <h3 className="font-bungee text-2xl uppercase tracking-widest">THE 5 FREQUENCIES</h3>
        </div>
        <div className="bg-zinc-950 border-4 border-zinc-900 rounded-[3rem] p-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {FREQUENCIES.map(freq => (
            <div key={freq.name} className="space-y-3 flex flex-col items-center text-center lg:items-start lg:text-left">
              <div className={`p-4 rounded-2xl bg-zinc-900 border-2 border-white/5 ${freq.color} shadow-lg`}>
                {freq.icon}
              </div>
              <h4 className={`text-xl font-bungee uppercase ${freq.color}`}>{freq.name}</h4>
              <p className="text-xs font-bold text-zinc-500 uppercase leading-loose tracking-widest">
                {freq.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Pro Tip */}
      <div className="bg-yellow-400 p-8 rounded-[2.5rem] flex flex-col md:flex-row items-center justify-between gap-6 shadow-[0_0_50px_rgba(250,196,21,0.2)]">
        <div className="flex items-center gap-6">
          <div className="p-4 bg-black rounded-2xl text-yellow-400">
            <Sparkles size={32} />
          </div>
          <div className="space-y-1">
            <h3 className="font-bungee text-2xl text-black uppercase leading-none">SIGMA PRO TIP</h3>
            <p className="text-[10px] font-black text-yellow-900 uppercase tracking-widest">COOK AFTER MIDNIGHT FOR HIGHER COGNITIVE DAMAGE POINTS.</p>
          </div>
        </div>
        <button 
          onClick={() => setActiveView('editor')}
          className="px-10 py-5 bg-black text-white font-bungee text-xl rounded-2xl hover:scale-105 active:scale-95 transition-all shadow-xl"
        >
          START COOKING
        </button>
      </div>
    </div>
  );
}
